import { useState } from 'react'
import { DocumentTextIcon, InformationCircleIcon } from '@heroicons/react/24/solid'
import { Modal } from '../common/Modal'
import TemplatePicker from './TemplatePicker'
import { TemplateObject } from '@neum/frontend-shared/types/templateTypes'
import VetRecButton from '../common/VetRecButton'
import { TemplateType } from '@neum/frontend-shared/constants/templateData'
import { postDefaultDischargeTemplate, postDefaultRecordsRecapTemplate, postDefaultTemplate } from '../../ServerActions'
import { useAuth, useUser } from '@clerk/clerk-react'
import { Spinner } from '../../utils/Spinner'

interface DefaultTemplateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm?: (templateId:string) => void;
    templateToPropose?: string;
    type?: string;
}

export default function DefaultTemplateModal({
    isOpen,
    onClose,
    onConfirm,
    templateToPropose,
    type = TemplateType.MEDICAL.toString(),
}: DefaultTemplateModalProps) {
    const [error, setError] = useState<string | undefined>(undefined);
    const [template, setTemplate] = useState<TemplateObject | undefined>(undefined);
    const [saving, setSaving] = useState(false);
    const { getToken } = useAuth();
    const { user } = useUser();

    const saveDefaultTemplate = async () => {
        setSaving(true);
        setError(undefined);
        try {
            if (!template) {
                setError('Please select a template before saving.');
                return;
            }
            if (type === TemplateType.MEDICAL.toString()) {
                await postDefaultTemplate(template.id, await getToken({template:"supabase"}) ?? "")   
            } else if (type === TemplateType.COMMUNICATIONS.toString()) {
                await postDefaultDischargeTemplate(template.id, await getToken({template:"supabase"}) ?? "")
            } else if (type === TemplateType.RECORDSRECAP.toString()) {
                await postDefaultRecordsRecapTemplate(template.id, await getToken({template:"supabase"}) ?? "")
            }
            await user?.reload()
            onConfirm?.(template.id);
            onClose();  
        } catch {
            setError('An error occurred while saving the default template. Please try again.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Set a Default Template`}
            subTitle={
                <>
                    <span>
                        Would you like to set this template as your default? This will be the template used by default when creating new notes.
                    </span>
                </>
            }
            icon={<DocumentTextIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />}
            maxWidth="sm:max-w-sm"
        >
            {error && (
                <div className="mt-2 bg-red-100 rounded-md p-4 text-sm text-red-900 text-center flex flex-row gap-x-3 justify-center items-center mx-auto">
                    <InformationCircleIcon className="h-5 w-5 text-red-600" />
                    {error}
                </div>
            )}

            <div className="mt-5 sm:mt-6 flex flex-col gap-y-2 justify-center items-center relative w-full">
                <TemplatePicker template={template} setTemplate={setTemplate} className='' pickerWidth="w-full" templateBackground='bg-white' templateIdForce={templateToPropose}/>
            </div>
            <div className="mt-5 flex flex-row gap-x-3">
                <VetRecButton
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 h-10 items-center"
                    onClick={onClose}
                    disabled={saving}
                >
                    Cancel
                </VetRecButton>
                <VetRecButton
                    type="button"
                    className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 h-10 items-center ${
                        saving ? 'cursor-not-allowed bg-gray-600' : 'cursor-pointer bg-blue-600 hover:bg-blue-500'
                    }`}
                    onClick={saveDefaultTemplate}
                    disabled={saving || !template}
                >
                    {saving ? <Spinner timer={false} size="h-5 w-5"/> : "Set Default"}
                </VetRecButton>
            </div>
        </Modal>
    );
}
